import type { TitleSize } from '@tempo-io/roadmap-dto';
import { cn } from '@tempo-io/sui-class-utils';
import { differenceInDays, isBefore } from 'date-fns';
import { type Dispatch, memo, type SetStateAction, useMemo, useRef } from 'react';
import { getItemTitleFontSize } from '@/components/ui/swimlane/layout/body/cards/CardUtils';
import type { LabelWithField } from '@/components/ui/swimlane/types';
import { progressFieldIdToProgress, systemProgressToProgress } from '../utils/itemProgressUtils';
import { TimelineItem } from './TimelineItem';
import { MIN_ITEM_WIDTH } from './TimelineItemConstants';
import { useExpandedChildrenBackground } from './useExpandedChildrenBackground';
import type { TimelineItemType, TimelineRowType } from './useTimelineTableColumns';
import { lightenTimelineBgColor } from './utils/timelineColorUtils';
import { getNormalizePlannedDates } from './utils/timelineDateUtils';
import {
  calculateItemWidth,
  calculateMaxContainerHeight,
  clampEndDateToWindow,
  clampStartDateToWindow,
  getItemHeight,
  getSingleLaneHeight,
  isOutsideTimelineWindow,
} from './utils/timelineGeometryUtils';
import { stackTimelineItems } from './utils/timelineStackingUtils';

export const TimelineItemContainer = memo(
  ({
    isCompact,
    canResize,
    canDragBetweenHeaders,
    canDragDates,
    timelineStartDate,
    timelineEndDate,
    item,
    isExpanded,
    setExpandedChildren,
    color,
    dayWidth,
    titleSize,
    intervalStart,
    onResizeEnd,
    onItemClick,
    row,
    itemColorById,
    containerTopOffset,
    progressFieldId,
    systemProgressMap,
    hideName = false,
    isPublishedMode = false,
    showDates = true,
    timelineItemClassName,
    timelineItemTextColor,
    labels,
    alignNameLeft = false,
    isSelected = false,
    isInMultiSelectDrag = false,
    getSelectedItemIds,
    onSelectionClick,
  }: {
    isCompact: boolean;
    canResize: boolean;
    canDragBetweenHeaders: boolean;
    canDragDates: boolean;
    timelineStartDate: string;
    timelineEndDate: string;
    item: TimelineItemType;
    isExpanded: boolean;
    setExpandedChildren: Dispatch<
      SetStateAction<{
        [key: number]: boolean;
      }>
    >;
    color: string;
    dayWidth: number;
    titleSize: TitleSize;
    intervalStart: Date;
    onResizeEnd?: (itemId: number, newStartDate: string, newEndDate: string) => void;
    onItemClick: (itemId: number) => void;
    row?: TimelineRowType;
    itemColorById?: Record<number, string>;
    containerTopOffset: number;
    progressFieldId: number | null;
    systemProgressMap: Map<number, number> | null;
    hideName?: boolean;
    isPublishedMode?: boolean;
    showDates?: boolean;
    timelineItemClassName?: string;
    timelineItemTextColor?: string;
    labels?: LabelWithField[];
    alignNameLeft?: boolean;
    isSelected?: boolean;
    isInMultiSelectDrag?: boolean;
    getSelectedItemIds?: () => number[] | null;
    onSelectionClick?: (
      itemId: number,
      modifiers: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean },
    ) => boolean;
  }) => {
    const itemRef = useRef<HTMLDivElement>(null);

    const {
      hasAnyDate: hasAnyPlannedDate,
      start: normalizedStart,
      finish: normalizedFinish,
    } = getNormalizePlannedDates(item.plannedStartDateTime, item.plannedFinishDateTime);

    const startDateToUse = clampStartDateToWindow(normalizedStart, timelineStartDate);
    const endDateToUse = clampEndDateToWindow(normalizedFinish, timelineEndDate);

    const calculatedWidth = calculateItemWidth(startDateToUse, endDateToUse, dayWidth);

    const hasVisibleLabels = !isCompact && (labels?.some((l) => Boolean(l?.value)) ?? false);
    const singleLaneHeight = getSingleLaneHeight(isCompact);
    const itemHeight = getItemHeight(isCompact, hasVisibleLabels);
    const itemTopOffset = (singleLaneHeight - itemHeight) / 2;
    const progress = systemProgressMap
      ? systemProgressToProgress(item.id, systemProgressMap)
      : progressFieldIdToProgress(progressFieldId ?? null, item);
    const progressPercentage: number | null =
      progress !== null ? Math.round(progress * 100) : progressFieldId !== null ? 0 : null;

    const leftOffset = differenceInDays(startDateToUse, intervalStart) * dayWidth;
    const fontSize = getItemTitleFontSize(titleSize);

    const { stackedItems: stackedChildren, highestOrder: maxChildOrder } = useMemo(() => {
      return item.children && item.children.length > 0
        ? stackTimelineItems(item.children)
        : { stackedItems: [], highestOrder: 0 };
    }, [item.children]);

    const { bgLeft, bgRight } = useExpandedChildrenBackground({
      isExpanded,
      stackedChildren,
      timelineStartDate,
      timelineEndDate,
      intervalStart,
      dayWidth,
      leftOffset,
      calculatedWidth,
    });

    if (
      isOutsideTimelineWindow(
        hasAnyPlannedDate,
        startDateToUse,
        endDateToUse,
        timelineStartDate,
        timelineEndDate,
      )
    ) {
      return null;
    }

    const maxContainerHeight = calculateMaxContainerHeight(
      singleLaneHeight,
      maxChildOrder,
      stackedChildren.length > 0,
    );
    const containerHeight = isExpanded ? maxContainerHeight : singleLaneHeight;

    const hasChildren = item.children && item.children.length > 0;

    return (
      <div
        ref={itemRef}
        className="absolute overflow-visible z-5 transition-[height] duration-50 ease-in-out"
        style={{
          height: containerHeight,
          maxHeight: maxContainerHeight,
          minHeight: singleLaneHeight,
          left: leftOffset,
          width: calculatedWidth,
          top: containerTopOffset,
        }}
      >
        <TimelineItem
          item={item}
          color={color}
          isCompact={isCompact}
          itemTopOffset={itemTopOffset}
          calculatedWidth={calculatedWidth}
          itemHeight={itemHeight}
          progress={progressPercentage}
          fontSize={fontSize}
          timelineStartDate={timelineStartDate}
          timelineEndDate={timelineEndDate}
          startDateToUse={startDateToUse}
          endDateToUse={endDateToUse}
          isExpanded={isExpanded}
          hasChildren={hasChildren}
          onExpandToggle={(e) => {
            e.stopPropagation();
            setExpandedChildren((prev) => ({
              ...prev,
              [item.id]: !isExpanded,
            }));
          }}
          canResize={canResize && (item.isEditable ?? false)}
          canDragBetweenHeaders={canDragBetweenHeaders && (item.isEditable ?? false)}
          canDragDates={canDragDates && (item.isEditable ?? false)}
          isChild={false}
          onItemClick={onItemClick}
          dayWidth={dayWidth}
          onResizeEnd={onResizeEnd}
          row={row}
          hideName={hideName}
          isPublishedMode={isPublishedMode}
          showDates={showDates}
          timelineItemClassName={timelineItemClassName}
          timelineItemTextColor={timelineItemTextColor}
          labels={labels}
          alignNameLeft={alignNameLeft}
          isSelected={isSelected}
          isInMultiSelectDrag={isInMultiSelectDrag}
          getSelectedItemIds={getSelectedItemIds}
          onSelectionClick={onSelectionClick}
        />

        {isExpanded && (
          <div className="absolute left-0 top-0 w-full">
            {/* Shaded background wrapping parent and child items */}
            <div
              className={cn('absolute z-[-1] border-l-3', {
                'rounded-lg': !isCompact,
                'rounded-sm': isCompact,
              })}
              style={(() => {
                const bgPadding = isCompact ? 4 : 10;
                const bgTop = itemTopOffset - bgPadding;
                const childrenBottom =
                  singleLaneHeight + maxChildOrder * singleLaneHeight + itemTopOffset + itemHeight;
                return {
                  backgroundColor: lightenTimelineBgColor(color, 90),
                  borderColor: lightenTimelineBgColor(color, 40),
                  top: bgTop,
                  height: childrenBottom + bgPadding - bgTop,
                  left: bgLeft - bgPadding,
                  right: bgRight - bgPadding,
                };
              })()}
            />

            {stackedChildren.map((child) => {
              const childDates = getNormalizePlannedDates(
                child.plannedStartDateTime,
                child.plannedFinishDateTime,
              );
              if (!childDates.hasAnyDate) {
                return null;
              }
              const childNS = childDates.start as string;
              const childNF = childDates.finish as string;

              const childStartDate = isBefore(childNS, timelineStartDate)
                ? timelineStartDate
                : childNS;

              const childEndDate = isBefore(childNF, timelineEndDate) ? childNF : timelineEndDate;

              if (
                isBefore(new Date(timelineEndDate), new Date(childStartDate)) ||
                isBefore(new Date(childEndDate), new Date(timelineStartDate))
              ) {
                return null;
              }

              const childNumDays = differenceInDays(childEndDate, childStartDate) + 1;
              const childWidth = Math.max(dayWidth * childNumDays, MIN_ITEM_WIDTH);
              const childLeftOffset = differenceInDays(childStartDate, intervalStart) * dayWidth;
              const childTopOffset = (child.order || 0) * singleLaneHeight + itemTopOffset;
              const colorToUse = itemColorById?.[child.id] || color;
              const childColor = lightenTimelineBgColor(colorToUse, 30);
              const childCanResize = canResize && (child.isEditable ?? false);
              const childCanDragBetweenHeaders =
                canDragBetweenHeaders && (child.isEditable ?? false);
              const childCanDragDates = canDragDates && (child.isEditable ?? false);
              const childRawProgress = systemProgressMap
                ? systemProgressToProgress(child.id, systemProgressMap)
                : progressFieldIdToProgress(progressFieldId ?? null, child);
              const childProgressPercentage: number | null =
                childRawProgress !== null
                  ? Math.round(childRawProgress * 100)
                  : progressFieldId !== null
                    ? 0
                    : null;

              return (
                <div
                  key={child.id}
                  className="absolute"
                  style={{
                    left: childLeftOffset - leftOffset,
                    width: childWidth,
                    top: singleLaneHeight,
                    height: singleLaneHeight,
                  }}
                >
                  <TimelineItem
                    item={child}
                    color={childColor}
                    isCompact={isCompact}
                    itemTopOffset={childTopOffset}
                    itemHeight={itemHeight}
                    progress={childProgressPercentage}
                    fontSize={fontSize}
                    timelineStartDate={timelineStartDate}
                    timelineEndDate={timelineEndDate}
                    startDateToUse={childStartDate}
                    endDateToUse={childEndDate}
                    canResize={childCanResize}
                    canDragBetweenHeaders={childCanDragBetweenHeaders}
                    canDragDates={childCanDragDates}
                    isChild
                    onItemClick={onItemClick}
                    dayWidth={dayWidth}
                    calculatedWidth={childWidth}
                    hideName={hideName}
                    isPublishedMode={isPublishedMode}
                    showDates={showDates}
                    timelineItemClassName={timelineItemClassName}
                    timelineItemTextColor={timelineItemTextColor}
                    alignNameLeft={alignNameLeft}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  },
);

TimelineItemContainer.displayName = 'TimelineItemContainer';
